import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  ActivityIndicator,
  TouchableOpacity,
  RefreshControl,
} from 'react-native';
import { useFocusEffect, useRouter, useNavigation } from 'expo-router';
import { teamsApi } from '../../../src/api/client';
import { useEncryptionStore } from '../../../src/stores/encryptionStore';
import { useTeamStore, Team } from '../../../src/stores/teamStore';
import { peekTeamName, resolveTeamNames } from '../../../src/utils/teamName';
import { getErrorMessage } from '../../../src/utils/errors';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import ScreenHeader from '../../../src/components/ScreenHeader';

const createStyles = (theme: Theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.bgSecondary,
  },
  centered: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 24,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 15,
    color: theme.colors.textMuted,
  },
  emptyText: {
    fontSize: 15,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
  },
  teamRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    backgroundColor: theme.colors.surface,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  teamInfo: {
    flex: 1,
  },
  teamName: {
    fontSize: 16,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  teamMeta: {
    fontSize: 13,
    color: theme.colors.textMuted,
  },
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    marginLeft: 10,
  },
  typeBadgePersonal: {
    backgroundColor: theme.colors.primaryBg,
  },
  typeBadgeShared: {
    backgroundColor: theme.colors.warningBg,
  },
  typeBadgeText: {
    fontSize: 11,
    fontWeight: '600' as const,
    textTransform: 'uppercase' as const,
  },
  typeBadgePersonalText: {
    color: theme.colors.primary,
  },
  typeBadgeSharedText: {
    color: theme.colors.warningText,
  },
  chevron: {
    fontSize: 18,
    color: theme.colors.textMuted,
    marginLeft: 8,
  },
  errorText: {
    fontSize: 15,
    color: theme.colors.error,
    textAlign: 'center' as const,
  },
});

export default function TeamsScreen() {
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const router = useRouter();
  const navigation = useNavigation();

  const [teams, setTeams] = useState<Team[]>([]);
  const [teamNames, setTeamNames] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const personalTeamId = useEncryptionStore((s) => s.personalTeamId);

  const fetchTeams = useCallback(async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    setError(null);

    try {
      const response = await teamsApi.getMyTeams();
      const fetched = response.data as Team[];
      setTeams(fetched);
      useTeamStore.setState({ teams: fetched });
      // Names are E2EE — decrypt in the background and fill in as they land
      resolveTeamNames(fetched).then(setTeamNames).catch(() => {});
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load teams.'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchTeams();
    }, [fetchTeams])
  );

  // Decrypted name once resolveTeamNames() lands; placeholder until then.
  const getTeamDisplayName = (team: Team): string =>
    teamNames[team.id] ?? peekTeamName(team);

  const renderTeam = ({ item }: { item: Team }) => {
    const isPersonal = item.team_type === 'personal';
    return (
      <TouchableOpacity
        style={styles.teamRow}
        onPress={() => {
          if (!isPersonal) {
            (navigation as any).push('team-detail', { teamId: item.id });
          }
        }}
        disabled={isPersonal}
        accessibilityLabel={`${getTeamDisplayName(item)}, ${isPersonal ? 'personal' : 'shared'} team`}
        accessibilityRole="button"
        testID={`teams.team-${item.id}`}
      >
        <View style={styles.teamInfo} accessible={false}>
          <Text style={styles.teamName} accessible={false}>
            {getTeamDisplayName(item)}
          </Text>
          <Text style={styles.teamMeta} accessible={false}>
            {isPersonal ? 'Your private workspace' : `Key version ${item.current_key_version}`}
          </Text>
        </View>
        <View
          style={[
            styles.typeBadge,
            isPersonal ? styles.typeBadgePersonal : styles.typeBadgeShared,
          ]}
          accessible={false}
        >
          <Text
            style={[
              styles.typeBadgeText,
              isPersonal ? styles.typeBadgePersonalText : styles.typeBadgeSharedText,
            ]}
            accessible={false}
          >
            {isPersonal ? 'Personal' : 'Shared'}
          </Text>
        </View>
        {!isPersonal && (
          <Text style={styles.chevron} accessible={false}>{'\u203A'}</Text>
        )}
      </TouchableOpacity>
    );
  };

  const headerView = (
    <ScreenHeader
      title="Teams"
      left={{
        label: 'Go back',
        icon: 'arrow-back',
        onPress: () => router.back(),
        testID: 'teams.back-button',
      }}
      right={{
        label: 'Create Team',
        text: '+ New',
        tone: 'accent',
        onPress: () => (navigation as any).push('team-create'),
        testID: 'teams.create-team-button',
      }}
    />  );

  if (isLoading) {
    return (
      <View style={styles.container}>
        {headerView}
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading teams...</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        {headerView}
        <View style={styles.centered}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      </View>
    );
  }

  // Sort: personal first, then shared
  const sorted = [...teams].sort((a, b) => {
    if (a.team_type === 'personal') return -1;
    if (b.team_type === 'personal') return 1;
    return 0;
  });

  return (
    <View style={styles.container}>
      {headerView}
      <FlatList
        data={sorted}
        keyExtractor={(item) => item.id}
        renderItem={renderTeam}
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={() => fetchTeams(true)} />
        }
        ListEmptyComponent={
          <View style={styles.centered}>
            <Text style={styles.emptyText}>
              No teams yet. Create a team to start collaborating.
            </Text>
          </View>
        }
        testID="teams.team-list"
      />
    </View>
  );
}
